import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Trophy, BookOpen, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

/* ------------- Pull from Wins (employee, self-review) ------------- */
export function PullFromWinsButton({ cycleId, onApply }) {
  const [open, setOpen] = useState(false);
  const [wins, setWins] = useState([]);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/wins")
      .then((r) => {
        const items = r.data.items || [];
        setWins(items);
        const init = {}; items.forEach((w) => { init[w.id] = true; });
        setPicked(init);
      })
      .catch(() => toast.error("Failed to load wins"))
      .finally(() => setLoading(false));
    setDraft("");
  }, [open]);

  const ids = Object.keys(picked).filter((k) => picked[k]);

  const run = async () => {
    if (!ids.length) return toast.error("Pick at least one win");
    setRunning(true);
    try {
      const r = await api.post("/wins/export-narrative", {
        win_ids: ids,
        cycle_id: cycleId || null,
        role_kind: "self",
      });
      setDraft(r.data.narrative || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setRunning(false);
    }
  };

  const apply = () => {
    if (!draft) return;
    onApply?.(draft);
    setOpen(false);
    toast.success("Pulled from your Wins");
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="pull-from-wins-btn">
        <Trophy className="w-3 h-3 mr-1.5 text-amber-500" /> Pull from Wins
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pull-wins-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Pull from your Wins Journal</DialogTitle>
            <DialogDescription>Pick the wins to weave into your self-review. We'll group them by goal.</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>
          ) : wins.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              No wins logged yet. Start your <a href="/wins" className="underline">Wins Journal</a> first.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2 border border-slate-200 rounded-lg p-3 max-h-72 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Wins ({ids.length}/{wins.length})</span>
                  <button
                    className="text-xs text-slate-500 hover:text-slate-900"
                    onClick={() => {
                      const all = ids.length === wins.length;
                      const next = {}; wins.forEach((w) => { next[w.id] = !all; });
                      setPicked(next);
                    }}
                    data-testid="pull-wins-toggle-all"
                  >{ids.length === wins.length ? "Clear" : "Select all"}</button>
                </div>
                {wins.map((w) => (
                  <label key={w.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer" data-testid={`pull-win-${w.id}`}>
                    <input type="checkbox" checked={!!picked[w.id]} onChange={(e) => setPicked((p) => ({ ...p, [w.id]: e.target.checked }))} className="mt-0.5" />
                    <span className="text-xs text-slate-700 line-clamp-2">{w.title}</span>
                  </label>
                ))}
              </div>
              <div className="lg:col-span-3 space-y-3">
                <Button onClick={run} disabled={running || !ids.length} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="run-pull-wins-btn">
                  {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Generate self-review draft
                </Button>
                {draft && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                    <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans leading-relaxed max-h-72 overflow-y-auto">{draft}</pre>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-pull-wins">Cancel</Button>
            <Button onClick={apply} disabled={!draft} data-testid="apply-pull-wins">
              <Check className="w-4 h-4 mr-1" /> Use as my self-summary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------- Pull from Coaching Log (manager review) ------------- */
export function PullFromCoachingButton({ subjectEmployeeId, cycleId, onApply }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!open || !subjectEmployeeId) return;
    setLoading(true);
    api.get("/coaching/notes", { params: { subject_employee_id: subjectEmployeeId } })
      .then((r) => {
        const items = r.data.items || [];
        setNotes(items);
        const init = {}; items.forEach((n) => { init[n.id] = true; });
        setPicked(init);
      })
      .catch(() => toast.error("Failed to load notes"))
      .finally(() => setLoading(false));
    setDraft(null);
  }, [open, subjectEmployeeId]);

  const ids = Object.keys(picked).filter((k) => picked[k]);

  const run = async () => {
    if (!ids.length) return toast.error("Pick at least one note");
    setRunning(true);
    try {
      const r = await api.post("/coaching/synthesize", {
        subject_employee_id: subjectEmployeeId,
        cycle_id: cycleId || null,
        note_ids: ids,
      });
      setDraft(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setRunning(false);
    }
  };

  const apply = () => {
    if (!draft?.narrative) return;
    onApply?.(draft.narrative);
    setOpen(false);
    toast.success("Pulled from your Coaching Log");
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="pull-from-coaching-btn">
        <BookOpen className="w-3 h-3 mr-1.5 text-violet-600" /> Pull from Coaching Log
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pull-coaching-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-violet-600" /> Pull from your Coaching Log</DialogTitle>
            <DialogDescription>Synthesize your private notes into a manager-review draft. Edit before submitting.</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>
          ) : notes.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              No notes for this report yet. Add some in the <a href="/coaching" className="underline">Coaching Log</a> first.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2 border border-slate-200 rounded-lg p-3 max-h-72 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Notes ({ids.length}/{notes.length})</span>
                  <button
                    className="text-xs text-slate-500 hover:text-slate-900"
                    onClick={() => {
                      const all = ids.length === notes.length;
                      const next = {}; notes.forEach((n) => { next[n.id] = !all; });
                      setPicked(next);
                    }}
                    data-testid="pull-coaching-toggle-all"
                  >{ids.length === notes.length ? "Clear" : "Select all"}</button>
                </div>
                {notes.map((n) => (
                  <label key={n.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer" data-testid={`pull-note-${n.id}`}>
                    <input type="checkbox" checked={!!picked[n.id]} onChange={(e) => setPicked((p) => ({ ...p, [n.id]: e.target.checked }))} className="mt-0.5" />
                    <span className="text-xs text-slate-700 line-clamp-2">{n.body}</span>
                  </label>
                ))}
              </div>
              <div className="lg:col-span-3 space-y-3">
                <Button onClick={run} disabled={running || !ids.length} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="run-pull-coaching-btn">
                  {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Generate manager-review draft
                </Button>
                {draft && (
                  <>
                    {draft.balance && (
                      <div className="text-xs text-slate-500 flex items-center gap-3">
                        <span><strong className="text-emerald-700">{draft.balance.positive}</strong> positive</span>
                        <span><strong className="text-slate-700">{draft.balance.neutral}</strong> neutral</span>
                        <span><strong className="text-amber-700">{draft.balance.developmental}</strong> developmental</span>
                      </div>
                    )}
                    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                      <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans leading-relaxed max-h-72 overflow-y-auto">{draft.narrative}</pre>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-pull-coaching">Cancel</Button>
            <Button onClick={apply} disabled={!draft?.narrative} data-testid="apply-pull-coaching">
              <Check className="w-4 h-4 mr-1" /> Use as my manager summary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
